/**
 * Where a label belongs on an irregular mask.
 *
 * Chips used to anchor at the mask's bounding-box centre, and the 2026-08-24 field
 * screenshots show what that does to real rooms: a floor wraps around the fixtures standing
 * on it, so its box centre — and therefore its chip — landed on the toilet. The right anchor
 * is the *visual centre*: the mask pixel farthest from any edge of the mask, which for a
 * floor is the widest stretch of visible floor and for a ring-framed mirror is the frame's
 * thickest run, never the hole.
 *
 * Computed with a two-pass chamfer distance transform over the same small hit grid the canvas
 * already keeps for tap-testing — O(cells), well under a millisecond at 160px wide.
 */
export function visualCenter(
  hit: Uint8Array,
  width: number,
  height: number,
): { x: number; y: number } | null {
  const size = width * height
  if (size === 0) return null
  const INF = 1 << 20
  const dist = new Int32Array(size)
  let any = false
  for (let i = 0; i < size; i += 1) {
    if (hit[i]) {
      dist[i] = INF
      any = true
    }
  }
  if (!any) return null

  // Forward pass (top-left neighbours), treating the frame's border as outside so a mask
  // touching the edge is not scored as if it extended forever.
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const i = y * width + x
      if (!dist[i]) continue
      let best = dist[i]!
      const up = y > 0 ? dist[i - width]! : 0
      const left = x > 0 ? dist[i - 1]! : 0
      best = Math.min(best, up + 1, left + 1)
      dist[i] = best
    }
  }
  // Backward pass (bottom-right neighbours).
  let bestIndex = -1
  let bestValue = -1
  for (let y = height - 1; y >= 0; y -= 1) {
    for (let x = width - 1; x >= 0; x -= 1) {
      const i = y * width + x
      if (!dist[i]) continue
      const down = y < height - 1 ? dist[i + width]! : 0
      const right = x < width - 1 ? dist[i + 1]! : 0
      const value = Math.min(dist[i]!, down + 1, right + 1)
      dist[i] = value
      if (value > bestValue) {
        bestValue = value
        bestIndex = i
      }
    }
  }
  if (bestIndex < 0) return null
  return { x: (bestIndex % width) + 0.5, y: Math.floor(bestIndex / width) + 0.5 }
}
